import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorText } from '@/components/ui';
import { expiryValid, luhnValid, mockPaymentMethodId } from '@/lib/card';
import { billingApi } from '@/lib/queries';

export type ConfirmCard = () => Promise<string>;

type Props = {
  mode: 'stripe' | 'mock';
  publishableKey: string | null;
  onReady: (confirm: ConfirmCard) => void;
};

/** Card number / MM/YY / CVC group. Stripe Elements when configured, a validated mock otherwise. */
export function CardFields({ mode, publishableKey, onReady }: Props) {
  if (mode === 'stripe')
    return publishableKey ? (
      <StripeCard publishableKey={publishableKey} onReady={onReady} />
    ) : (
      // Live billing must never fall back to the test card form ("try 4242").
      <ErrorText>
        The card form isn’t available right now. Refresh the page or try again shortly.
      </ErrorText>
    );
  return <MockCard onReady={onReady} />;
}

const ELEMENT_STYLE = {
  style: {
    base: {
      fontFamily: 'Manrope, Helvetica, Arial, sans-serif',
      fontSize: '14px',
      color: '#111',
      '::placeholder': { color: '#999' },
    },
  },
};

const stripeCache = new Map<string, Promise<Stripe | null>>();

function StripeCard({
  publishableKey,
  onReady,
}: {
  publishableKey: string;
  onReady: (c: ConfirmCard) => void;
}) {
  const promise = useMemo(() => {
    let p = stripeCache.get(publishableKey);
    if (!p) {
      p = loadStripe(publishableKey);
      stripeCache.set(publishableKey, p);
    }
    return p;
  }, [publishableKey]);
  return (
    <Elements stripe={promise}>
      <StripeInner onReady={onReady} />
    </Elements>
  );
}

function StripeInner({ onReady }: { onReady: (c: ConfirmCard) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    if (!stripe || !elements) return;
    onReady(async () => {
      const card = elements.getElement(CardNumberElement);
      if (!card) throw new Error('Card form is not ready');
      const { clientSecret } = await billingApi.setupIntent();
      const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } });
      if (result.error) throw new Error(result.error.message ?? 'Card was declined');
      const pm = result.setupIntent.payment_method;
      return typeof pm === 'string' ? pm : (pm?.id ?? '');
    });
  }, [stripe, elements, onReady]);
  const handlers = { onFocus: () => setFocus(true), onBlur: () => setFocus(false) };
  return (
    <div className={`cardgroup${focus ? ' focus' : ''}`}>
      <div className="stripe-el">
        <CardNumberElement
          options={{ ...ELEMENT_STYLE, placeholder: 'Card number' }}
          {...handlers}
        />
      </div>
      <div className="stripe-el">
        <CardExpiryElement options={ELEMENT_STYLE} {...handlers} />
      </div>
      <div className="stripe-el">
        <CardCvcElement options={ELEMENT_STYLE} {...handlers} />
      </div>
    </div>
  );
}

function MockCard({ onReady }: { onReady: (c: ConfirmCard) => void }) {
  const number = useRef('');
  const exp = useRef('');
  const cvc = useRef('');
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    onReady(() => {
      if (!luhnValid(number.current))
        return Promise.reject(new Error('Enter a valid card number (try 4242 4242 4242 4242)'));
      if (!expiryValid(exp.current))
        return Promise.reject(new Error('Enter a valid expiry as MM / YY'));
      if (!/^\d{3,4}$/.test(cvc.current.trim()))
        return Promise.reject(new Error('Enter the 3 or 4 digit CVC'));
      return Promise.resolve(mockPaymentMethodId(number.current, exp.current));
    });
  }, [onReady]);
  const handlers = { onFocus: () => setFocus(true), onBlur: () => setFocus(false) };
  return (
    <div className={`cardgroup${focus ? ' focus' : ''}`}>
      <input
        placeholder="Card number"
        inputMode="numeric"
        autoComplete="cc-number"
        aria-label="Card number"
        onChange={(e) => (number.current = e.target.value)}
        {...handlers}
      />
      <input
        placeholder="MM / YY"
        inputMode="numeric"
        autoComplete="cc-exp"
        aria-label="Expiry"
        onChange={(e) => (exp.current = e.target.value)}
        {...handlers}
      />
      <input
        placeholder="CVC"
        inputMode="numeric"
        autoComplete="cc-csc"
        aria-label="CVC"
        onChange={(e) => (cvc.current = e.target.value)}
        {...handlers}
      />
    </div>
  );
}
